import React, { useState, useEffect } from "react";
import { getAuthToken } from "../context/AuthContext";
import {
  X,
  User,
  BookOpen,
  Phone,
  Layers,
  Award,
  ClipboardList,
  CalendarCheck,
  TrendingUp,
  Info,
  Calendar,
  CheckCircle,
  FileCheck2,
  Lock,
  RotateCcw
} from "lucide-react";

interface Student {
  id: number;
  srn: string;
  student_name: string;
  father_name: string | null;
  mother_name: string | null;
  gender: string;
  dob: string | null;
  board_name: string;
  board_code: string;
  class_name: string;
  section: string;
  academic_session_name: string;
  medium: string;
  roll_number: string | null;
  student_mobile: string | null;
  parent_mobile: string | null;
  school_id?: number;
  school_name?: string;
  school_code?: string;
  block?: string;
  block_name?: string;
  district?: string;
  state?: string;
  status: string;
  created_at: string;
}

interface StudentProfileModalProps {
  isOpen: boolean;
  student: Student | null;
  onClose: () => void;
}

export const StudentProfileModal: React.FC<StudentProfileModalProps> = ({
  isOpen,
  student,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"profile" | "assessments" | "sat" | "attendance" | "analytics">("profile");
  const [marksHistory, setMarksHistory] = useState<any[]>([]);
  const [loadingMarks, setLoadingMarks] = useState<boolean>(false);
  const [attendanceProfile, setAttendanceProfile] = useState<any | null>(null);
  const [loadingAttendance, setLoadingAttendance] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && student?.id) {
      setLoadingMarks(true);
      setLoadingAttendance(true);
      const token = getAuthToken();

      fetch(`/api/students/${student.id}/marks-history`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.history) {
            setMarksHistory(data.history);
          }
        })
        .catch(err => console.error("Failed to load student marks history:", err))
        .finally(() => setLoadingMarks(false));

      fetch(`/api/attendance/student/${student.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.student) {
            setAttendanceProfile(data.student);
          }
        })
        .catch(err => console.error("Failed to load student attendance:", err))
        .finally(() => setLoadingAttendance(false));
    }
  }, [isOpen, student?.id]);

  if (!isOpen || !student) return null;

  const assessmentRecords = marksHistory.filter(h => h.type === "ASSESSMENT");
  const satRecords = marksHistory.filter(h => h.type === "SAT");

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0F2C59] to-[#1E3A8A] text-white p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-300 hover:text-white p-1 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-full bg-white/10 border-2 border-amber-400 flex items-center justify-center text-xl font-bold text-amber-300 shrink-0">
              {student.student_name.charAt(0)}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-bold">{student.student_name}</h2>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                  student.status === "active" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-red-500/20 text-red-300"
                }`}>
                  {student.status.toUpperCase()}
                </span>
              </div>
              <div className="text-xs text-slate-200 flex flex-wrap gap-x-3 gap-y-1 mt-1">
                <span>SRN: <strong className="font-mono text-amber-300">{student.srn}</strong></span>
                <span>•</span>
                <span>{student.board_name}</span>
                <span>•</span>
                <span>{student.class_name} - Section {student.section}</span>
                <span>•</span>
                <span>Roll: {student.roll_number || "—"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 px-6 bg-slate-50 text-xs font-semibold overflow-x-auto">
          <button
            onClick={() => setActiveTab("profile")}
            className={`py-2.5 px-3 border-b-2 transition flex items-center space-x-1.5 shrink-0 ${
              activeTab === "profile"
                ? "border-[#0F2C59] text-[#0F2C59] font-bold"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <User className="w-3.5 h-3.5" />
            <span>Student Profile</span>
          </button>
          <button
            onClick={() => setActiveTab("assessments")}
            className={`py-2.5 px-3 border-b-2 transition flex items-center space-x-1.5 shrink-0 ${
              activeTab === "assessments"
                ? "border-[#0F2C59] text-[#0F2C59] font-bold"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5" />
            <span>Assessment Tests</span>
          </button>
          <button
            onClick={() => setActiveTab("sat")}
            className={`py-2.5 px-3 border-b-2 transition flex items-center space-x-1.5 shrink-0 ${
              activeTab === "sat"
                ? "border-[#0F2C59] text-[#0F2C59] font-bold"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Award className="w-3.5 h-3.5" />
            <span>SAT Exams</span>
          </button>
          <button
            onClick={() => setActiveTab("attendance")}
            className={`py-2.5 px-3 border-b-2 transition flex items-center space-x-1.5 shrink-0 ${
              activeTab === "attendance"
                ? "border-[#0F2C59] text-[#0F2C59] font-bold"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <CalendarCheck className="w-3.5 h-3.5" />
            <span>Attendance</span>
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`py-2.5 px-3 border-b-2 transition flex items-center space-x-1.5 shrink-0 ${
              activeTab === "analytics"
                ? "border-[#0F2C59] text-[#0F2C59] font-bold"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Board Exam Improvement</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "profile" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {/* 1. Identity Information */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                  <div className="flex items-center space-x-2 font-bold text-slate-800 border-b border-slate-200 pb-2">
                    <User className="w-4 h-4 text-[#0F2C59]" />
                    <span>Personal & Identity Details</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <span className="text-slate-500 block">Full Name</span>
                      <span className="font-semibold text-slate-900">{student.student_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">SRN (Unique)</span>
                      <span className="font-mono font-bold text-[#0F2C59]">{student.srn}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Father Name</span>
                      <span className="font-medium text-slate-800">{student.father_name || "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Mother Name</span>
                      <span className="font-medium text-slate-800">{student.mother_name || "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Gender</span>
                      <span className="font-medium text-slate-800">{student.gender}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Date of Birth</span>
                      <span className="font-medium text-slate-800">{student.dob || "—"}</span>
                    </div>
                  </div>
                </div>

                {/* 2. Academic Enrollment */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                  <div className="flex items-center space-x-2 font-bold text-slate-800 border-b border-slate-200 pb-2">
                    <BookOpen className="w-4 h-4 text-[#0F2C59]" />
                    <span>Academic Enrollment</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <span className="text-slate-500 block">Board</span>
                      <span className="font-bold text-blue-900">{student.board_name} ({student.board_code})</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Class & Section</span>
                      <span className="font-bold text-slate-900">{student.class_name} - {student.section}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Instruction Medium</span>
                      <span className="font-medium text-slate-800">{student.medium}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Roll Number</span>
                      <span className="font-mono font-bold text-slate-900">{student.roll_number || "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Academic Session</span>
                      <span className="font-bold text-amber-800 font-mono">{student.academic_session_name || "2026-27"}</span>
                    </div>
                  </div>
                </div>

                {/* 3. Contact Details */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                  <div className="flex items-center space-x-2 font-bold text-slate-800 border-b border-slate-200 pb-2">
                    <Phone className="w-4 h-4 text-[#0F2C59]" />
                    <span>Communication & Contact</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <span className="text-slate-500 block">Student Mobile</span>
                      <span className="font-mono text-slate-800">{student.student_mobile ? `📱 ${student.student_mobile}` : "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Parent / Guardian Mobile</span>
                      <span className="font-mono text-slate-800">{student.parent_mobile ? `📱 ${student.parent_mobile}` : "—"}</span>
                    </div>
                  </div>
                </div>

                {/* 4. Institution Record */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                  <div className="flex items-center space-x-2 font-bold text-slate-800 border-b border-slate-200 pb-2">
                    <Layers className="w-4 h-4 text-[#0F2C59]" />
                    <span>School Institution & Block</span>
                  </div>
                  <div className="text-[11px] space-y-1">
                    <div className="font-semibold text-slate-900">{student.school_name || "Government Senior Secondary School, Nuh"}</div>
                    <div className="text-slate-600 font-medium">School Code: <span className="font-mono text-[#0F2C59] font-bold">{student.school_code || "HR-NUH-0001"}</span> • Block: <span className="font-bold text-slate-800">{student.block || student.block_name || "Nuh"}</span></div>
                    <div className="text-slate-500">District: {student.district || "Nuh"}, {student.state || "Haryana"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assessment & SAT Performance View */}
          {(activeTab === "assessments" || activeTab === "sat") && (
            <div className="space-y-4 text-xs">
              {(activeTab === "assessments" ? assessmentRecords : satRecords).length === 0 ? (
                <div className="p-8 text-center bg-slate-50 border border-dashed border-slate-300 rounded-xl space-y-2">
                  <Info className="w-6 h-6 text-slate-400 mx-auto" />
                  <p className="font-bold text-slate-700">No {activeTab === "sat" ? "SAT" : "Assessment"} Marks Recorded Yet</p>
                  <p className="text-[11px] text-slate-400">Marks entered by subject teachers will appear dynamically here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(activeTab === "assessments" ? assessmentRecords : satRecords).map((ass) => (
                    <div key={ass.id} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-3">
                        <div className="flex items-center gap-2">
                          {ass.type === "SAT" ? <Award className="w-4 h-4 text-amber-600" /> : <FileCheck2 className="w-4 h-4 text-blue-700" />}
                          <span className="font-bold text-slate-900 text-sm">{ass.name}</span>
                          <span className="text-[11px] text-slate-400">• Max Marks: {ass.maximum_marks}</span>
                        </div>
                        {ass.exam_date && <span className="text-[11px] text-slate-500 font-medium">{ass.exam_date}</span>}
                      </div>

                      {ass.subjects.length === 0 ? (
                        <p className="text-[11px] text-slate-400 italic">No marks entered for this exam yet.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {ass.subjects.map((sub: any) => (
                            <div key={sub.id} className="p-2.5 bg-white rounded-lg border border-slate-200 flex items-center justify-between">
                              <div>
                                <div className="font-semibold text-slate-800">{sub.subject_name}</div>
                                <div className="text-[10px] text-slate-400">
                                  Status: <span className="font-semibold capitalize text-slate-600">{sub.status}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                {sub.is_absent ? (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-900 font-extrabold rounded text-[11px]">AB</span>
                                ) : sub.obtained_marks !== null ? (
                                  <div>
                                    <span className="font-bold text-slate-900 text-sm">{sub.obtained_marks}</span>
                                    <span className="text-slate-400 text-[10px]"> / {ass.maximum_marks}</span>
                                    <div className="text-[10px] font-bold text-blue-700">{sub.percentage}%</div>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 italic text-[11px]">Not Entered</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Attendance Profile */}
          {activeTab === "attendance" && (
            <div className="space-y-4">
              {loadingAttendance ? (
                <div className="p-8 text-center text-slate-500 text-xs">Loading student attendance profile...</div>
              ) : !attendanceProfile || attendanceProfile.total_days === 0 ? (
                <div className="p-8 text-center bg-slate-50 border border-dashed border-slate-300 rounded-xl space-y-2">
                  <CalendarCheck className="w-8 h-8 text-slate-400 mx-auto" />
                  <div className="font-bold text-slate-700 text-sm">No Attendance Recorded</div>
                  <p className="text-xs text-slate-500">No attendance sessions recorded for this student yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Attendance KPI Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="text-[10px] font-bold text-slate-500 uppercase">Working Days</div>
                      <div className="text-xl font-bold font-mono text-slate-900 mt-0.5">
                        {attendanceProfile.total_days}
                      </div>
                    </div>
                    <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-lg">
                      <div className="text-[10px] font-bold text-emerald-800 uppercase">Days Present</div>
                      <div className="text-xl font-bold font-mono text-emerald-700 mt-0.5">
                        {attendanceProfile.present_days}
                      </div>
                    </div>
                    <div className="p-3 bg-rose-50/70 border border-rose-200 rounded-lg">
                      <div className="text-[10px] font-bold text-rose-800 uppercase">Days Absent</div>
                      <div className="text-xl font-bold font-mono text-rose-700 mt-0.5">
                        {attendanceProfile.absent_days}
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg border ${
                      attendanceProfile.attendance_percentage < 50
                        ? "bg-rose-100 border-rose-300"
                        : attendanceProfile.attendance_percentage < 75
                        ? "bg-amber-100 border-amber-300"
                        : "bg-emerald-100 border-emerald-300"
                    }`}>
                      <div className="text-[10px] font-bold text-slate-700 uppercase">Overall %</div>
                      <div className="text-xl font-black font-mono text-[#0F2C59] mt-0.5">
                        {attendanceProfile.attendance_percentage}%
                      </div>
                    </div>
                  </div>

                  {/* Monthly Table */}
                  <div className="bg-slate-50/70 rounded-xl border border-slate-200 p-3 space-y-2">
                    <div className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                      Academic Session Monthly Breakdown
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-200/60 text-slate-700 font-bold border-b border-slate-200">
                            <th className="py-2 px-2.5">Month</th>
                            <th className="py-2 px-2.5 text-center">Working Days</th>
                            <th className="py-2 px-2.5 text-center">Present</th>
                            <th className="py-2 px-2.5 text-center">Absent</th>
                            <th className="py-2 px-2.5 text-right">Attendance %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                          {attendanceProfile.monthly_breakdown?.map((m: any, idx: number) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="py-2 px-2.5 font-bold text-slate-900">{m.month_name}</td>
                              <td className="py-2 px-2.5 text-center font-mono">{m.working_days}</td>
                              <td className="py-2 px-2.5 text-center font-mono text-emerald-700 font-bold">{m.present}</td>
                              <td className="py-2 px-2.5 text-center font-mono text-rose-700 font-bold">{m.absent}</td>
                              <td className="py-2 px-2.5 text-right font-mono font-bold text-[#0F2C59]">{m.percentage}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Placeholders for future phases */}
          {activeTab === "analytics" && (
            <div className="p-8 text-center bg-slate-50 border border-dashed border-slate-300 rounded-xl space-y-3">
              <Info className="w-8 h-8 text-slate-400 mx-auto" />
              <div className="font-bold text-slate-700 text-sm">
                No data available yet
              </div>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                District-wide predictive board exam analytics will activate in Phase 7.
              </p>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
            <button
              onClick={onClose}
              className="bg-[#0F2C59] hover:bg-[#081B38] text-white text-xs font-bold py-2 px-5 rounded-lg transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
